import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { Instrument } from '@dabba/contracts';
import { api } from '../api/client';
import { useTicks } from '../data/DataProvider';
import { instrumentSub, instrumentTitle } from '../lib/instrumentLabel';
import { IconClose, IconSearch } from './icons';
import { Spinner } from './common';
import { Change, LiveNum } from './pro';

const RECENT_KEY = 'dabba-recent-searches';
const RECENT_MAX = 6;

function readRecents(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? (parsed.filter((s) => typeof s === 'string') as string[]) : [];
  } catch {
    return [];
  }
}

function pushRecent(symbol: string): void {
  try {
    const next = [symbol, ...readRecents().filter((s) => s !== symbol)].slice(0, RECENT_MAX);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    // storage unavailable — recents are a convenience, never a requirement
  }
}

/**
 * The app's primary navigation. Opens on ⌘K / Ctrl-K or from the nav bar, and
 * is fully keyboard-driven: type, arrow, Enter. Results come from the server's
 * instrument master, so anything listed is reachable.
 */
export function SearchOverlay({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [q, setQ] = useState('');
  const [cursor, setCursor] = useState(0);
  const [debounced, setDebounced] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // One request per pause in typing, not one per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 180);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    if (!open) return;
    setQ('');
    setDebounced('');
    setCursor(0);
    // Focus after paint, or the browser hands focus back to the trigger.
    const t = setTimeout(() => inputRef.current?.focus(), 20);
    return () => clearTimeout(t);
  }, [open]);

  const { data, isFetching } = useQuery({
    queryKey: ['search', debounced],
    queryFn: () => api.searchInstruments(debounced),
    enabled: open && debounced.length >= 1,
    staleTime: 60_000,
  });

  const recents = useMemo(() => (open ? readRecents() : []), [open]);
  const { data: recentMeta } = useQuery({
    queryKey: ['instrumentsMeta', recents.join(',')],
    queryFn: () => api.instrumentsMeta(recents),
    enabled: open && debounced.length === 0 && recents.length > 0,
    staleTime: 6 * 60 * 60 * 1000,
  });

  const results: Instrument[] =
    debounced.length >= 1 ? (data?.results ?? []) : (recentMeta?.results ?? []);

  // Live prices on the results themselves. Picking a symbol out of a list is
  // a decision, and a trader making it wants the number in front of them
  // rather than one navigation away. The source is reference-counted, so this
  // subscription costs nothing once the overlay closes and `results` empties.
  const ticks = useTicks(results.slice(0, 24).map((r) => r.symbol));

  const go = (symbol: string): void => {
    pushRecent(symbol);
    onClose();
    navigate(`/stocks/${encodeURIComponent(symbol)}`);
  };

  const onKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Escape') {
      onClose();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor((c) => Math.min(c + 1, results.length - 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
      return;
    }
    if (e.key === 'Enter') {
      const pick = results[cursor];
      if (pick) go(pick.symbol);
    }
  };

  if (!open) return null;

  return (
    <div
      className="overlay overlay-top"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal modal-wide" role="dialog" aria-label="Search instruments">
        <div className="search-box">
          <IconSearch size={19} className="muted" />
          <input
            ref={inputRef}
            value={q}
            placeholder="Search stocks, indices, F&O contracts…"
            onChange={(e) => {
              setQ(e.target.value);
              setCursor(0);
            }}
            onKeyDown={onKeyDown}
            aria-label="Search by symbol or company name"
          />
          {isFetching ? <Spinner /> : null}
          <button className="icon-btn" onClick={onClose} aria-label="Close search">
            <IconClose />
          </button>
        </div>

        <div className="search-results">
          {debounced.length === 0 && results.length === 0 ? (
            <div className="empty t-sm">Start typing a symbol — RELIANCE, NIFTY, TCS…</div>
          ) : null}

          {debounced.length === 0 && results.length > 0 ? (
            <div className="label" style={{ padding: '11px 18px 5px' }}>
              Recent
            </div>
          ) : null}

          {debounced.length >= 1 && !isFetching && results.length === 0 ? (
            <div className="empty t-sm">
              Nothing matches “{debounced}”. Instrument search needs the daily master to be loaded.
            </div>
          ) : null}

          {results.map((r, i) => (
            <button
              key={r.symbol}
              className={`search-result${i === cursor ? ' cursor' : ''}`}
              onMouseEnter={() => setCursor(i)}
              onClick={() => go(r.symbol)}
            >
              <div className="grow" style={{ minWidth: 0 }}>
                <div className="list-title nowrap" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {instrumentTitle(r.symbol, r)}
                </div>
                <div className="q-sub">{instrumentSub(r) ?? r.name}</div>
              </div>
              <div className="right" style={{ minWidth: 108 }}>
                <LiveNum value={ticks[r.symbol]?.ltp} className="t-md semibold" />
                <div className="t-xs">
                  <Change tick={ticks[r.symbol]} />
                </div>
              </div>
            </button>
          ))}
        </div>

        <div className="search-foot">
          <span>
            <span className="kbd">↑</span>
            <span className="kbd">↓</span> move
          </span>
          <span>
            <span className="kbd">↵</span> open
          </span>
          <span>
            <span className="kbd">esc</span> close
          </span>
          <span style={{ marginLeft: 'auto' }}>
            {results.length > 0 ? `${results.length} instrument${results.length === 1 ? '' : 's'}` : 'Equities · Indices · F&O'}
          </span>
        </div>
      </div>
    </div>
  );
}
